// src/pages/admin/AdminImport.jsx
import React, { useState, useRef } from "react";
import {
  Box, Paper, Stack, Typography, Button, Switch, FormControlLabel,
  Alert, Divider, Chip, CircularProgress, alpha,
} from "@mui/material";
import UploadFileIcon    from "@mui/icons-material/UploadFile";
import DownloadIcon      from "@mui/icons-material/Download";
import CheckCircleIcon   from "@mui/icons-material/CheckCircle";
import ErrorIcon         from "@mui/icons-material/Error";
import PlayArrowIcon     from "@mui/icons-material/PlayArrow";
import VisibilityIcon    from "@mui/icons-material/Visibility";
import API from "../../api";
import { useToast } from "../../context/ToastContext";
import { C, gradientText } from "../../theme";

export default function AdminImport() {
  const toast = useToast();
  const inputRef = useRef();
  const [file,    setFile]    = useState(null);
  const [dry,     setDry]     = useState(true);
  const [report,  setReport]  = useState(null);
  const [loading, setLoading] = useState(false);

  const onFileChange = e => { setFile(e.target.files?.[0] || null); setReport(null); };
  const onDrop = e => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f && (f.name.endsWith(".csv") || f.name.endsWith(".json") || f.name.endsWith(".jsonl"))) { setFile(f); setReport(null); }
    else toast.error("Format accepté : CSV, JSON, JSONL");
  };

  const onUpload = async () => {
    if (!file) { toast.error("Choisissez un fichier."); return; }
    setLoading(true); setReport(null);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("dry_run", String(dry));
    try {
      const { data } = await API.post("/imports/questions", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setReport(data);
      if (dry) toast.info("Validation terminée — aucune donnée insérée.");
      else     toast.success(`Import réussi : ${data.inserted ?? data.created ?? "?"} questions importées.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Import échoué."); }
    finally { setLoading(false); }
  };

  const downloadTemplate = async () => {
    try {
      const res = await fetch(`${API.defaults.baseURL}/imports/template.csv`);
      const blob = await res.blob();
      const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "template_questions.csv"; a.click();
    } catch { toast.error("Téléchargement impossible."); }
  };

  const ok      = report && !report.errors?.length;
  const errCount = report?.errors?.length ?? 0;

  return (
    <Box>
      <Stack direction={{ xs:"column", sm:"row" }} justifyContent="space-between" alignItems={{ sm:"center" }} sx={{ mb:4 }} spacing={2}>
        <Box>
          <Typography variant="overline" color="text.secondary">OUTILS</Typography>
          <Typography variant="h4" fontWeight={700}><Box component="span" sx={gradientText}>Import de questions</Box></Typography>
          <Typography variant="caption" color="text.secondary">CSV, JSON ou JSONL</Typography>
        </Box>
        <Button variant="outlined" startIcon={<DownloadIcon />} onClick={downloadTemplate}>
          Template CSV
        </Button>
      </Stack>

      {/* Zone de drop */}
      <Paper onDragOver={e => e.preventDefault()} onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        sx={{
          p:6, mb:3, textAlign:"center", cursor:"pointer", borderRadius:"14px",
          border:`2px dashed ${file ? C.indigo[400] : C.slate[300]}`,
          background: file ? alpha(C.indigo[500],.04) : "white",
          transition:"all .2s",
          "&:hover": { borderColor:C.indigo[400], background:alpha(C.indigo[500],.04) },
        }}>
        <input ref={inputRef} type="file" accept=".csv,application/json,.jsonl" style={{ display:"none" }} onChange={onFileChange} />
        <UploadFileIcon sx={{ fontSize:52, color: file ? C.indigo[500] : C.slate[300], mb:1.5 }} />
        {file ? (
          <>
            <Typography fontWeight={700} sx={{ color:C.indigo[700] }}>{file.name}</Typography>
            <Typography variant="caption" color="text.secondary">{(file.size/1024).toFixed(1)} KB · Cliquez pour changer</Typography>
          </>
        ) : (
          <>
            <Typography fontWeight={600} color="text.secondary">Glissez un fichier ici</Typography>
            <Typography variant="caption" color="text.secondary">ou cliquez pour parcourir — CSV, JSON, JSONL</Typography>
          </>
        )}
      </Paper>

      {/* Options */}
      <Paper sx={{ p:3, mb:3 }}>
        <Stack spacing={2}>
          <FormControlLabel
            control={<Switch checked={dry} onChange={e => { setDry(e.target.checked); setReport(null); }} />}
            label={
              <Box>
                <Typography variant="body2" fontWeight={600}>Mode validation (dry-run)</Typography>
                <Typography variant="caption" color="text.secondary">
                  {dry ? "Aucune donnée ne sera insérée — idéal pour vérifier votre fichier avant l'import réel." : "Les questions seront insérées en base de données."}
                </Typography>
              </Box>
            } />
          <Divider />
          <Stack direction="row" spacing={2}>
            <Button variant="contained" size="large" disabled={!file || loading}
              startIcon={loading ? <CircularProgress size={18} color="inherit" /> : dry ? <VisibilityIcon /> : <PlayArrowIcon />}
              onClick={onUpload}>
              {loading ? "Traitement…" : dry ? "Valider le fichier" : "Lancer l'import"}
            </Button>
            {file && <Button variant="outlined" onClick={() => { setFile(null); setReport(null); if(inputRef.current) inputRef.current.value=""; }}>Réinitialiser</Button>}
          </Stack>
        </Stack>
      </Paper>

      {/* Rapport */}
      {report && (
        <Paper sx={{ p:3, borderRadius:"14px" }}>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb:2 }}>
            {ok ? <CheckCircleIcon sx={{ color:C.teal[600] }} /> : <ErrorIcon sx={{ color:C.red[500] }} />}
            <Typography variant="h6" fontWeight={700}>
              {dry ? "Rapport de validation" : "Rapport d'import"}
            </Typography>
          </Stack>

          <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mb:2.5 }}>
            {report.total     !== undefined && <Chip label={`${report.total} lignes traitées`}   size="small" sx={{ fontWeight:700 }} />}
            {report.valid     !== undefined && <Chip label={`${report.valid} valides`}            size="small" sx={{ background:alpha(C.teal[500],.1), color:C.teal[700], fontWeight:700 }} />}
            {report.inserted  !== undefined && <Chip label={`${report.inserted} insérées`}        size="small" sx={{ background:alpha(C.teal[500],.1), color:C.teal[700], fontWeight:700 }} />}
            {report.created   !== undefined && <Chip label={`${report.created} créées`}           size="small" sx={{ background:alpha(C.teal[500],.1), color:C.teal[700], fontWeight:700 }} />}
            {errCount > 0     && <Chip label={`${errCount} erreur${errCount>1?"s":""}`}           size="small" sx={{ background:alpha(C.red[500],.1), color:C.red[700], fontWeight:700 }} />}
          </Stack>

          {errCount > 0 && (
            <Stack spacing={1} sx={{ mb:2.5 }}>
              {report.errors.slice(0,10).map((e, i) => (
                <Alert key={i} severity="error" sx={{ borderRadius:2, py:.5 }}>{typeof e === "string" ? e : JSON.stringify(e)}</Alert>
              ))}
              {errCount > 10 && <Typography variant="caption" color="text.secondary">… et {errCount-10} autre{errCount-10>1?"s":""} erreur{errCount-10>1?"s":""}.</Typography>}
            </Stack>
          )}

          {dry && !errCount && (
            <Alert severity="success" sx={{ borderRadius:2, mb:2 }}>
              Fichier valide ! Désactivez le mode validation puis relancez pour importer réellement.
            </Alert>
          )}

          <Box sx={{ position:"relative" }}>
            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ mb:.5, display:"block", textTransform:"uppercase", letterSpacing:"0.08em" }}>
              Réponse brute du serveur
            </Typography>
            <Box component="pre" sx={{
              p:2, borderRadius:2, background:C.slate[900]||"#0f172a", color:"#a3e635",
              fontSize:"0.78rem", overflowX:"auto", fontFamily:"'Courier New', monospace",
            }}>
              {JSON.stringify(report, null, 2)}
            </Box>
          </Box>
        </Paper>
      )}
    </Box>
  );
}
